export const FOOTER_CTA = {
  eyebrow: 'Ready to explore?',
  headline: 'Capital and everyday finance, one platform',
  button: 'Join the waitlist',
} as const;

export const SITE = {
  name: 'Hello Gwiji',
  url: 'https://hellogwiji.com',
  tagline: 'Capital partnerships and everyday finance, one platform',
  description:
    'Connect with verified founders, move money securely, and pay where life happens, guided every step of the way.',
  supportEmail: 'support@hellogwiji.com',
  calendlyUrl: 'https://calendly.com/hellogwiji-support/30min',
  location: 'Nairobi, Kenya',
  waitlistApi: 'https://gateway.hellogwiji.com/configuration/waitlist/',
} as const;

/** Primary navigation, shown in the header */
export const HEADER_NAV = [
  { label: 'Home', href: '/' },
  { label: 'Application', href: '/application' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
] as const;

/** Footer page links */
export const FOOTER_NAV = [
  { label: 'Application', href: '/application' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '/contact' },
] as const;

/** Legal links, footer only */
export const LEGAL_NAV = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms-and-conditions' },
  { label: 'Age Suitability', href: '/age-suitability' },
] as const;

/** @deprecated Use HEADER_NAV, FOOTER_NAV, or LEGAL_NAV */
export const SITE_NAV = [...FOOTER_NAV, ...LEGAL_NAV] as const;

export const NAV_LINKS = HEADER_NAV;

export const HERO_COUNTER = {
  prefix: '',
  value: 100,
  suffix: '%',
  label: 'Transparent terms on every partnership',
} as const;

/** High-resolution Unsplash images for large hero/parallax sections */
function unsplashHero(photoId: string, alt: string, width = 3200, height = 2133) {
  return {
    image: `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=${width}&h=${height}&q=95`,
    alt,
  } as const;
}

export const HERO_WEBSITE_CDN = 'https://hello-gwiji.fra1.cdn.digitaloceanspaces.com/website';

/** Hero deck assets - one AVIF per card */
export const HERO_WEBSITE_IMAGES = {
  build: `${HERO_WEBSITE_CDN}/build.avif`,
  grow: `${HERO_WEBSITE_CDN}/grow.avif`,
  manage: `${HERO_WEBSITE_CDN}/manage.avif`,
  pay: `${HERO_WEBSITE_CDN}/pay.avif`,
  save: `${HERO_WEBSITE_CDN}/save.avif`,
} as const;

/** Hero deck - order matches fan layout left to center to right */
const HERO_DECK_CARDS = [
  { slug: 'pay', image: HERO_WEBSITE_IMAGES.pay, title: 'Discover' },
  { slug: 'manage', image: HERO_WEBSITE_IMAGES.manage, title: 'Explore' },
  { slug: 'save', image: HERO_WEBSITE_IMAGES.save, title: 'Live' },
  { slug: 'build', image: HERO_WEBSITE_IMAGES.build, title: 'Build' },
  { slug: 'grow', image: HERO_WEBSITE_IMAGES.grow, title: 'Grow' },
] as const;

/** Hero scroll deck - product screens from CDN */
export const HOME_HERO_DECK = HERO_DECK_CARDS.map((card) => ({
  slug: card.slug,
  image: card.image,
  alt: `${card.title} in the Hello Gwiji app`,
  title: card.title,
}));

export const HOME_HERO = {
  kicker: 'Lifestyle fintech',
  title: 'MONEY THAT WORKS BETTER TOGETHER',
  subtitle:
    'Build financial goals with the people who matter to you',
  image: HOME_HERO_DECK[2].image,
  imageAlt: HOME_HERO_DECK[2].alt,
  flanks: {
    left: [HOME_HERO_DECK[1], HOME_HERO_DECK[0]],
    right: [HOME_HERO_DECK[3], HOME_HERO_DECK[4]],
  },
} as const;

export const HOME_HERO_DECK_CTA = {
  title: 'Built for every kind of ambition',
  text: 'Discover opportunities, explore who to build with, and grow what matters together',
} as const;

export const FINANCE_SECTION = {
  title: 'Meet Hello Gwiji',
  subtitle: 'A simpler way to manage money, together',
  cardOneImage: `${HERO_WEBSITE_CDN}/working.avif`,
  cardOneImageAlt: 'Hello Gwiji app, partnerships and payments',
  cardOneHeadline: 'Discover what money can do when people come together',
  cardTwoImage: `${HERO_WEBSITE_CDN}/venture.avif`,
  cardTwoImageAlt: 'Hello Gwiji venture screen',
  cardTwoTitle: 'Venture Layer',
  cardTwoTag: 'some goals are better achieved together',
  cardThreeImage: `${HERO_WEBSITE_CDN}/fintech-layer-for-pay.avif`,
  cardThreeImageAlt: 'Hello Gwiji fintech layer for pay',
  cardThreeHeadline: 'Your everyday financial companion',
} as const;

export const FINANCE_HUB = {
  eyebrow: 'Platform overview',
  title: 'Your capital partnership hub',
  subtitle:
    'From discovery to agreement, everything you need to connect investors and entrepreneurs in one guided platform.',
  cards: [
    {
      image: '/images/maps.png',
      tag: 'Manage partnerships that grow smarter every single day.',
      title: 'Venture & investor matching',
      description:
        'Easily connect verified entrepreneurs with interested investors through detailed profiles, business plans, and funding needs.',
      large: true,
    },
    {
      image: '/images/app.png',
      tag: 'Turn interest into structured, fair capital agreements.',
      title: 'Regulated fintech wallet',
      description:
        'All money movements including deposits, holds, releases, and profit distributions happen securely within our platform.',
      large: false,
    },
  ],
} as const;

export const STORY_SECTION = {
  eyebrow: 'Meet Hello Gwiji',
  title: 'Building meaningful connections beyond boundaries',
  description:
    'From Nairobi to the world, Hello Gwiji connects people, ventures, and opportunities, bringing investors and entrepreneurs closer and making every partnership more transparent.',
  image: '/images/IMG_1026.JPG',
} as const;

export const MONEY_SECTION = {
  title: 'Money made simple',
  subtitle:
    'Daily spending and investing made simple, so you can focus on what matters',
  quote: 'Less hassle. More possibilities.',
  quoteSub: 'Transparent from day one.',
  quoteImage: `${HERO_WEBSITE_CDN}/less-paper-work.avif`,
  quoteImageAlt: 'Less paperwork, more living with Hello Gwiji',
  cardTwoLabel: 'Explore opportunities',
  cardTwoSub: 'Find something worth being part of',
  cardTwoTagline: 'See what others are building',
  cardThreeTitle: 'Join the waitlist',
  cardThreeButton: 'Get Started',
  cardThreeImage: '/images/money-waitlist.jpg',
} as const;

export const REASON_SECTION = {
  title: 'where everyday capital meets real opportunity',
  subtitle: 'Discover ventures that are a good match for your capital',
  bullets: ['Flexible timeframes', 'Verified entrepreneurs', 'Terms documented upfront'],
  image: '/images/mapp.png',
  overlayText: 'Discover verified ventures you believe in',
  cta: 'Reach the summit',
} as const;

export const MILESTONES_SECTION = {
  title: 'Built for daily life',
  subtitle: 'One app. for managing today and building tomorrow, together',
} as const;

export const MILESTONES = [
  {
    title: 'For investors',
    badge: 'Put capital to work',
    description: 'Access vetted opportunities and partner directly with verified entrepreneurs.',
    image: '/images/Financial-Planning-BG.webp',
    counter: { value: 100, suffix: '%', label: 'Terms documented upfront' },
  },
  {
    title: 'For entrepreneurs',
    badge: 'Raise fair capital',
    description: 'Access capital without expensive bank loans or hard-to-reach angel networks.',
    image: '/images/Hello Gwiji Bridge.png',
    counter: { value: 2, suffix: '', label: 'Layers working together' },
  },
  {
    title: 'For everyone',
    badge: 'Grow together',
    description: 'Returns distributed according to agreed terms. We stay alongside to ensure success.',
    image: '/images/Financial-Planning-Saving-BG.webp',
    counter: { value: 24, suffix: '/7', label: 'Secure platform infrastructure' },
  },
] as const;

/** Milestones Achieved section, top card images */
export const MILESTONES_ACHIEVED_CARDS = [
  {
    title: 'Build Your Safety Net',
    description:
      'Put money aside for the moments that matter',
    image: `${HERO_WEBSITE_CDN}/first.avif`,
    alt: 'Piggy bank and coins representing a growing savings safety net',
  },
  {
    title: 'go further together',
    description:
      'Pool capital to pursue shared goals together through venture groups',
    image: `${HERO_WEBSITE_CDN}/middle.avif`,
    alt: 'Entrepreneurs and partners meeting to plan and fund a growing business',
  },
  {
    title: 'Spend Smarter, Live Better',
    description:
      'Stay on top of your everyday spending and see where every cent goes',
    image: `${HERO_WEBSITE_CDN}/third.avif`,
    alt: 'Person making an everyday card payment while managing spending',
  },
] as const;

export const PAY_SECTION = {
  ...unsplashHero(
    'photo-1521791136064-7986c2920216',
    'Diverse business partners shaking hands in a modern office',
  ),
  connectImage: `${HERO_WEBSITE_CDN}/hand-shake.avif`,
  connectImageAlt: 'Handshake connecting partners on Hello Gwiji',
} as const;

export const POWER_SECTION = {
  title: 'From discovery to capital',
  subtitle: 'Discover opportunities, build trusted ventures, and move forward together',
  image: HERO_WEBSITE_IMAGES.grow,
  imageAlt: 'Grow with Hello Gwiji',
} as const;

export const POWER_FEATURES = [
  {
    title: 'Discover',
    description:
      'Find ventures and opportunities that match your interests',
  },
  {
    title: 'Connect',
    description:
      'Express interest, engage founders and form secure partnerships',
  },
  {
    title: 'Agree',
    description:
      'Set terms and make decisions together before transacting capital',
  },
  {
    title: 'Grow',
    description:
      'Invest, track returns and build wealth together',
  },
] as const;

export const APP_BENEFITS = [
  '01 — Move between your devices without losing your place',
  '02 — Stay connected to your everyday money and ventures wherever you are',
  '03 — Everything feels familiar, whether you’re on your phone, tablet or computer',
  '04 — At home, at work or on the move, Hello Gwiji fits naturally into your day',
] as const;

export const FAQ_ITEMS = [
  {
    q: 'What is Hello Gwiji?',
    a: 'Hello Gwiji is a transparent platform that connects investors with verified entrepreneurs, and gives everyone a regulated wallet for everyday pay, savings, and pooling. We guide both parties from introduction to agreement using a Venture Layer for matching and a Fintech Layer for secure money movement.',
  },
  {
    q: 'How does the waitlist work?',
    a: 'Join the waitlist with your name, email, and interest area. We\'ll notify you when the platform launches and give you early access to create your profile and explore opportunities.',
  },
  {
    q: 'Is Hello Gwiji regulated?',
    a: 'Hello Gwiji operates with a regulated fintech wallet system. All transactions are tracked, recorded, and visible to both parties. Digital contracts are stored immutably before any funds move.',
  },
  {
    q: 'Who can use Hello Gwiji?',
    a: 'Investors, founders, everyday users who want to pay and save, people pooling with friends, and businesses that want to get paid can all join the waitlist.',
  },
  {
    q: 'Are there hidden fees?',
    a: 'No hidden structures. All terms are documented upfront and mutually agreed. We believe in clear, transparent partnerships with honest collaboration on both sides.',
  },
  {
    q: 'Where is Hello Gwiji based?',
    a: 'Hello Gwiji is based in Nairobi, Kenya, serving investors and entrepreneurs building productive capital partnerships across the region and beyond.',
  },
] as const;

export const PAYMENT_SCENES_SECTION = {
  title: 'Built for real life',
  subtitle:
    'From supermarket aisles to venture funding, pay, pool, and move money where life happens.',
  scenes: [
    {
      category: 'Games & entertainment',
      tag: 'Level up nights out',
      description: 'Tickets, top-ups, and nights out. Pay without leaving the moment.',
      image: `${HERO_WEBSITE_CDN}/games-and-entertainment.avif`,
      alt: 'Games and entertainment with Hello Gwiji',
    },
    {
      category: 'Fuel station',
      tag: 'Fill up & go',
      description: 'Pay at the pump or counter and get back on the road in seconds.',
      image: `${HERO_WEBSITE_CDN}/fuel-station.avif`,
      alt: 'Fuel station payment with Hello Gwiji',
    },
    {
      category: 'Salons & barbers',
      tag: 'Look sharp, pay easy',
      description: 'Confirm your appointment and settle when the cut or style is done.',
      image: `${HERO_WEBSITE_CDN}/salons-and-barbers.avif`,
      alt: 'Salons and barbers with Hello Gwiji',
    },
    {
      category: 'Supermarkets',
      tag: 'Cart to checkout',
      description: 'Scan, pay, and bag your groceries without the cash shuffle.',
      image: `${HERO_WEBSITE_CDN}/supermarkets.avif`,
      alt: 'Supermarket checkout with Hello Gwiji',
    },
    {
      category: 'Fruit parlors',
      tag: 'Fresh finds, fast pay',
      description: 'Pay produce vendors and market stalls without hunting for change.',
      image: `${HERO_WEBSITE_CDN}/fruit-parlors.avif`,
      alt: 'Fruit parlor payment with Hello Gwiji',
    },
    {
      category: 'Hotel',
      tag: 'Check in, settle out',
      description: 'Room, dining, and extras. Settle from your phone before you leave.',
      image: `${HERO_WEBSITE_CDN}/hotel.avif`,
      alt: 'Hotel stay payment with Hello Gwiji',
    },
    {
      category: 'Cafes',
      tag: 'Sip & tap',
      description: 'Coffee and pastries paid in one tap, no waiting for change.',
      image: `${HERO_WEBSITE_CDN}/cafes.avif`,
      alt: 'Cafe payment with Hello Gwiji',
    },
    {
      category: 'Fast food joints',
      tag: 'Grab & go',
      description: 'Order ahead, pay in-app, and pick up when your meal is ready.',
      image: `${HERO_WEBSITE_CDN}/fast-food-joints.avif`,
      alt: 'Fast food payment with Hello Gwiji',
    },
    {
      category: 'Restaurants',
      tag: 'Dinner, done',
      description: 'Split the bill or settle the table after dinner in one tap.',
      image: `${HERO_WEBSITE_CDN}/restaurants.avif`,
      alt: 'Restaurant payment with Hello Gwiji',
    },
    {
      category: 'Tour companies',
      tag: 'Book the adventure',
      description: 'Safaris, city tours, and trips. Book and pay with confidence.',
      image: `${HERO_WEBSITE_CDN}/tour-companies.avif`,
      alt: 'Tour company booking with Hello Gwiji',
    },
    {
      category: 'Investor funding',
      tag: 'Back bold ideas',
      description: 'Fund verified ventures with clear terms before capital moves.',
      image: `${HERO_WEBSITE_CDN}/investor-funding.avif`,
      alt: 'Investor funding with Hello Gwiji',
    },
    {
      category: 'Group pooling',
      tag: 'Split it fair',
      description: 'Pool with friends for trips, gifts, rent, or shared goals.',
      image: `${HERO_WEBSITE_CDN}/group-pooling.avif`,
      alt: 'Group pooling with Hello Gwiji',
    },
  ],
} as const;

/** @deprecated Use PAYMENT_SCENES_SECTION */
export const TESTIMONIALS = [
  {
    quote:
      'Finally a platform that treats capital partnerships like partnerships, not transactions. The transparency is exactly what the market needed.',
    name: 'Investor perspective',
    handle: 'Early waitlist member',
  },
  {
    quote:
      'As an entrepreneur, accessing fair capital without jumping through endless hoops is a game changer. Hello Gwiji gets it.',
    name: 'Entrepreneur perspective',
    handle: 'Early waitlist member',
  },
  {
    quote:
      'The two-layer approach of venture matching plus regulated wallets gives me confidence that both sides are protected.',
    name: 'Platform explorer',
    handle: 'Early waitlist member',
  },
  {
    quote:
      'Digital contracts, auditable communications, and clear terms. This is how capital partnerships should work in 2026.',
    name: 'Business owner',
    handle: 'Early waitlist member',
  },
] as const;

export const INTEGRATIONS_SECTION = {
  eyebrow: 'Platform',
  title: 'Four layers.',
  titleAccent: ' One platform.',
  subtitle: 'Manage everyday money, discover opportunities and build together, ALL IN ONE PLACE',
  image: `${HERO_WEBSITE_CDN}/platform.avif`,
  imageAlt: 'Hello Gwiji platform',
  tabs: [
    {
      id: 'venture',
      title: 'Venture Layer',
      description: 'Connect investors and founders. Browse, match, agree on terms.',
      accent: '#1e380f',
    },
    {
      id: 'fintech',
      title: 'Fintech Layer',
      description: 'Regulated wallet. Deposits, holds, releases, all tracked.',
      accent: '#2d5016',
    },
    {
      id: 'contracts',
      title: 'Digital contracts',
      description: 'Agreements stored immutably before any transfer.',
      accent: '#3d6b1e',
    },
    {
      id: 'pay',
      title: 'Everyday pay',
      description: 'Markets, cafes, and hotels. Pay where life happens.',
      accent: '#4a7c23',
    },
  ],
} as const;

export const INTEGRATIONS = [
  {
    title: 'Venture Layer',
    description:
      'Connect investors and entrepreneurs. Browse profiles, express interest, and agree on transparent partnership terms.',
  },
  {
    title: 'Fintech Layer',
    description:
      'Move money securely with regulated wallets. Deposits, holds, releases, withdrawals, and profit distributions are all tracked.',
  },
  {
    title: 'Digital contracts',
    description:
      'Immutable agreement storage with encoded payment schedules and conditions before any funds are transferred.',
  },
  {
    title: 'Real-time tracking',
    description:
      'Fraud protection, secure accounts, and live visibility into every transaction for both investors and entrepreneurs.',
  },
] as const;

export const WAITLIST_INTERESTS = [
  {
    value: 'put-money-to-work',
    title: 'Put money to work',
    subtitle: 'Partner with ventures, grow value',
    icon: 'investor',
  },
  {
    value: 'need-capital',
    title: 'Need capital for a venture',
    subtitle: 'Raise funds, find partners',
    icon: 'entrepreneur',
  },
  {
    value: 'everyday-pay',
    title: 'Pay and spend every day',
    subtitle: 'Markets, cafes, fuel, tap to pay',
    icon: 'wallet',
  },
  {
    value: 'pool-together',
    title: 'Pool money with others',
    subtitle: 'Split bills, rent, trips, shared goals',
    icon: 'pool',
  },
  {
    value: 'business-profile',
    title: 'Get paid as a business',
    subtitle: 'Create a profile, receive payments',
    icon: 'building',
  },
] as const;

export type WaitlistInterest = (typeof WAITLIST_INTERESTS)[number]['value'];
